/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports a hook, not a React component. */
import * as React from 'react';

import { treeComboboxKeyAction } from './tree-combobox-key-action.js';
import type { DimeTreeComboboxNode, DimeTreeComboboxRow } from './tree-combobox-model.js';
import {
  ancestorIdsOf,
  includedByAncestor,
  toggleTreeSelection,
  visibleTreeRows,
} from './tree-combobox-model.js';

/** The active row, else the first non-muted-ancestor row once the active one is gone or unset. */
function resolveActiveIndex(rows: readonly DimeTreeComboboxRow[], activeId: string | null) {
  const current = rows.findIndex((row) => row.node.id === activeId);
  return current === -1 ? rows.findIndex((row) => !row.isAncestorOnly) : current;
}

/**
 * Search, expansion and active-row state for one open popup; it mounts with the popup, so every
 * open starts with an empty search and only the ancestors of checked nodes expanded (CT-3).
 */
export function useTreeComboboxState(options: {
  roots: readonly DimeTreeComboboxNode[];
  value: readonly string[];
  onValueChange: (value: string[]) => void;
}) {
  const { roots, value, onValueChange } = options;
  const [query, setQuery] = React.useState('');
  const [expandedIds, setExpandedIds] = React.useState<ReadonlySet<string>>(() =>
    ancestorIdsOf({ roots, ids: value }),
  );
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const rows = React.useMemo(
    () => visibleTreeRows({ roots, expandedIds, query }),
    [roots, expandedIds, query],
  );
  const included = React.useMemo(() => includedByAncestor({ roots, value }), [roots, value]);
  const isSearching = query.trim() !== '';

  const activeIndex = resolveActiveIndex(rows, activeId);
  const activeRow = rows[activeIndex];

  const changeQuery = (next: string) => {
    setQuery(next);
    setActiveId(null);
  };

  const toggleExpanded = (id: string) => {
    setActiveId(id);
    if (isSearching) return;
    setExpandedIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  const toggleSelected = (id: string) => {
    setActiveId(id);
    if (included.has(id)) return;
    onValueChange(toggleTreeSelection({ roots, value, id }));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const action = treeComboboxKeyAction({
      key: event.key,
      rows,
      activeIndex,
      hasQueryText: query !== '',
    });
    if (!action) return;
    event.preventDefault();
    if (action.type === 'move') setActiveId(action.id);
    else if (action.type === 'toggle') toggleSelected(action.id);
    else toggleExpanded(action.id);
  };

  return {
    query,
    changeQuery,
    rows,
    included,
    isSearching,
    activeRow,
    toggleExpanded,
    toggleSelected,
    handleKeyDown,
  };
}
